import {
  enableClickHouseDataExport,
  enableFullPageUiForDatabricksWarehouseNativeIntegration,
  enableFullPageUiForRedshiftNativeExperimentation,
  enableNewUISnowflakeDataExport,
  enableS3DataExport,
} from '@gonfalon/dogfood-flags/dogfood-flags';

export enum DestinationKinds {
  Kinesis = 'kinesis',
  Pubsub = 'google-pubsub',
  Segment = 'segment',
  MParticle = 'mparticle',
  Azure = 'azure-event-hubs',
  Snowflake = 'snowflake',
  SnowflakeV2 = 'snowflake-v2',
  SnowflakeNativeExperimentation = 'snowflake-experimentation',
  Databricks = 'databricks',
  DatabricksNativeExperimentation = 'databricks-experimentation',
  BigQuery = 'bigquery',
  BigQueryNativeExperimentation = 'bigquery-experimentation',
  Redshift = 'redshift',
  RedshiftNativeExperimentation = 'redshift-experimentation',
  ClickHouse = 'clickhouse',
  ClickHouseNativeExperimentation = 'clickhouse-experimentation',
  S3 = 's3',
}
export const KINESIS = DestinationKinds.Kinesis;
export const PUBSUB = DestinationKinds.Pubsub;
export const SEGMENT = DestinationKinds.Segment;
export const MPARTICLE = DestinationKinds.MParticle;
export const AZURE = DestinationKinds.Azure;
export const SNOWFLAKE = DestinationKinds.Snowflake;
export const SNOWFLAKE_V2 = DestinationKinds.SnowflakeV2;
export const SNOWFLAKE_NATIVE_EXPERIMENTATION = DestinationKinds.SnowflakeNativeExperimentation;
export const DATABRICKS = DestinationKinds.Databricks;
export const BIGQUERY = DestinationKinds.BigQuery;
export const REDSHIFT = DestinationKinds.Redshift;
export const CLICKHOUSE = DestinationKinds.ClickHouse;
export const S3 = DestinationKinds.S3;
export const DEFAULT_CLICKHOUSE_PORT = '9440';
export const DEFAULT_S3_FILE_FORMAT = 'parquet';
export const AMAZON_REGIONS = [
  {
    code: 'us-east-1',
    name: 'N Virginia',
  },
  {
    code: 'us-east-2',
    name: 'Ohio',
  },
  {
    code: 'us-west-1',
    name: 'N California',
  },
  {
    code: 'us-west-2',
    name: 'Oregon',
  },
  {
    code: 'ca-central-1',
    name: 'Canada',
  },
  {
    code: 'eu-west-1',
    name: 'Ireland',
  },
  {
    code: 'eu-west-2',
    name: 'London',
  },
  {
    code: 'eu-west-3',
    name: 'Paris',
  },
  {
    code: 'eu-central-1',
    name: 'Frankfurt',
  },
  {
    code: 'ap-northeast-1',
    name: 'Tokyo',
  },
  {
    code: 'ap-northeast-2',
    name: 'Seoul',
  },
  {
    code: 'ap-northeast-3',
    name: 'Osaka',
  },
  {
    code: 'ap-southeast-1',
    name: 'Singapore',
  },
  {
    code: 'ap-southeast-2',
    name: 'Sydney',
  },
  {
    code: 'ap-south-1',
    name: 'Mumbai',
  },
  {
    code: 'sa-east-1',
    name: 'Sao Paulo',
  },
  {
    code: 'af-south-1',
    name: 'Cape Town',
  },
];

export const MPARTICLE_ENVIRONMENTS = ['production', 'development'];

// Warehouse integrations that currently support full-page create/edit flow.
const WAREHOUSE_FULL_PAGE_INTEGRATIONS: ReadonlySet<string> = new Set([
  DestinationKinds.SnowflakeNativeExperimentation,
  DestinationKinds.SnowflakeV2,
  DestinationKinds.BigQuery,
  DestinationKinds.BigQueryNativeExperimentation,
  DestinationKinds.ClickHouseNativeExperimentation,
  DestinationKinds.Redshift,
  DestinationKinds.Databricks,
] as const);

/**
 * Full-page integration UI is gated by:
 * 1) a static integration allowlist and
 * 2) per-integration dogfood flags evaluated at call-time where needed.
 *
 * Keep flag evaluation inside this function (instead of module scope) so the
 * value does not go stale before the LD client initializes or when targeting changes.
 */
export const isFullPageUiEnabledForWarehouseIntegration = (integrationKey: string): boolean => {
  if (integrationKey === DestinationKinds.SnowflakeV2) {
    return enableNewUISnowflakeDataExport();
  }
  if (integrationKey === DestinationKinds.ClickHouse) {
    return enableClickHouseDataExport();
  }
  if (integrationKey === DestinationKinds.DatabricksNativeExperimentation) {
    return enableFullPageUiForDatabricksWarehouseNativeIntegration();
  }
  if (integrationKey === DestinationKinds.RedshiftNativeExperimentation) {
    return enableFullPageUiForRedshiftNativeExperimentation();
  }
  if (integrationKey === DestinationKinds.S3) {
    return enableS3DataExport();
  }
  return WAREHOUSE_FULL_PAGE_INTEGRATIONS.has(integrationKey);
};

/**
 * Retry policy for short eventual-consistency windows after creating full-page
 * destination configurations.
 */
export const DESTINATION_CREATE_REDIRECT_RETRY_DELAYS_MS = [500, 1000, 2000, 4000] as const;
export const DESTINATION_CREATE_REDIRECT_RETRY_WINDOW_MS = 30_000;

export const SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY = SNOWFLAKE_NATIVE_EXPERIMENTATION;
export const SNOWFLAKE_DATA_EXPORT_INTEGRATION_KEY = SNOWFLAKE_V2;
export const BIGQUERY_DATA_EXPORT_INTEGRATION_KEY = DestinationKinds.BigQuery;
export const BIGQUERY_EXPERIMENTATION_INTEGRATION_KEY = DestinationKinds.BigQueryNativeExperimentation;
export const DATABRICKS_DATA_EXPORT_INTEGRATION_KEY = DestinationKinds.Databricks;
export const DATABRICKS_EXPERIMENTATION_INTEGRATION_KEY = DestinationKinds.DatabricksNativeExperimentation;
export const REDSHIFT_DATA_EXPORT_INTEGRATION_KEY = DestinationKinds.Redshift;
export const REDSHIFT_EXPERIMENTATION_INTEGRATION_KEY = DestinationKinds.RedshiftNativeExperimentation;
export const CLICKHOUSE_EXPERIMENTATION_INTEGRATION_KEY = DestinationKinds.ClickHouseNativeExperimentation;
export const S3_DATA_EXPORT_INTEGRATION_KEY = DestinationKinds.S3;

/**
 * Two-step warehouse setup lifecycle marker, stored inside a config's `configValues`
 * (mirrors the backend `helpers.SetupStatus*` constants). A config created via `/setup`
 * is `pending_setup` until `/complete-setup` flips it to `active`.
 *
 * The full-page architecture keys "pending draft vs finished config" off this — NOT the
 * generic `enabled` flag. `enabled` is the on/off toggle and can't tell "never finished
 * setup" apart from "finished but turned off", so relying on it would misclassify a
 * completed-but-disabled config as a draft.
 */
export const SETUP_STATUS_KEY = '_setupStatus';
export const SETUP_STATUS_PENDING = 'pending_setup';
export const SETUP_STATUS_ACTIVE = 'active';

export const FULL_PAGE_DESTINATION_CONFIGURATION_KINDS = [
  DestinationKinds.SnowflakeV2,
  DestinationKinds.BigQuery,
  DestinationKinds.ClickHouse,
  DestinationKinds.Redshift,
  DestinationKinds.Databricks,
  DestinationKinds.S3,
] as const;
const FULL_PAGE_DESTINATION_CONFIGURATION_KINDS_SET: ReadonlySet<string> = new Set(
  FULL_PAGE_DESTINATION_CONFIGURATION_KINDS,
);

export const isFullPageDestinationConfigurationKind = (kind: string): boolean =>
  FULL_PAGE_DESTINATION_CONFIGURATION_KINDS_SET.has(kind);

export const MPARTICLE_USERID_TYPES = [
  'customer_id',
  'email',
  'facebook',
  'twitter',
  'google',
  'microsoft',
  'mpid',
  'other',
  'other2',
  'other3',
  'other4',
];

export const BIGQUERY_LOCATIONS = [
  { code: 'US', name: 'United States (multi-region)' },
  { code: 'EU', name: 'European Union (multi-region)' },
  { code: 'us-central1', name: 'Iowa' },
  { code: 'us-east1', name: 'South Carolina' },
  { code: 'us-east4', name: 'Northern Virginia' },
  { code: 'us-east5', name: 'Columbus, Ohio' },
  { code: 'us-south1', name: 'Dallas' },
  { code: 'us-west1', name: 'Oregon' },
  { code: 'us-west2', name: 'Los Angeles' },
  { code: 'us-west3', name: 'Salt Lake City' },
  { code: 'us-west4', name: 'Las Vegas' },
  { code: 'northamerica-northeast1', name: 'Montréal' },
  { code: 'northamerica-northeast2', name: 'Toronto' },
  { code: 'northamerica-south1', name: 'Mexico' },
  { code: 'southamerica-east1', name: 'São Paulo' },
  { code: 'southamerica-west1', name: 'Santiago' },
  { code: 'europe-west1', name: 'Belgium' },
  { code: 'europe-west2', name: 'London' },
  { code: 'europe-west3', name: 'Frankfurt' },
  { code: 'europe-west4', name: 'Netherlands' },
  { code: 'europe-west6', name: 'Zürich' },
  { code: 'europe-west8', name: 'Milan' },
  { code: 'europe-west9', name: 'Paris' },
  { code: 'europe-west12', name: 'Turin' },
  { code: 'europe-north1', name: 'Finland' },
  { code: 'europe-southwest1', name: 'Madrid' },
  { code: 'europe-central2', name: 'Warsaw' },
  { code: 'asia-east1', name: 'Taiwan' },
  { code: 'asia-east2', name: 'Hong Kong' },
  { code: 'asia-northeast1', name: 'Tokyo' },
  { code: 'asia-northeast2', name: 'Osaka' },
  { code: 'asia-northeast3', name: 'Seoul' },
  { code: 'asia-south1', name: 'Mumbai' },
  { code: 'asia-south2', name: 'Delhi' },
  { code: 'asia-southeast1', name: 'Singapore' },
  { code: 'asia-southeast2', name: 'Jakarta' },
  { code: 'asia-southeast3', name: 'Bangkok' },
  { code: 'australia-southeast1', name: 'Sydney' },
  { code: 'australia-southeast2', name: 'Melbourne' },
  { code: 'me-central1', name: 'Doha' },
  { code: 'me-central2', name: 'Dammam' },
  { code: 'me-west1', name: 'Tel Aviv' },
  { code: 'africa-south1', name: 'Johannesburg' },
];

export const PRODUCT_ANALYTICS_INTEGRATIONS_PATH = 'product-analytics/integrations/';

export enum ProductAnalyticsWarehouseId {
  Snowflake = 'snowflake',
  BigQuery = 'bigquery',
  Databricks = 'databricks',
}

/**
 * Placeholder shown for never-persisted warehouse secret fields (the Databricks access token and
 * Hive bucket secret key, and the BigQuery WIF client secret). This mirrors the backend's
 * `maskedSecret` (internal/reps2/destreps/destinations.go): the API returns this value for those
 * fields on read, and the adapters seed the same value into the optimistic create→edit destination
 * so the form shows the masked placeholder immediately, before the canonical record reconciles
 * into Redux.
 */
export const MASKED_SECRET_PLACEHOLDER = '***********';
